import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import AdminDetails from "./AdminDetails";
import { COLORS, Delta, KpiCard, Skeleton, formatChf, formatDateTime, formatMoney, formatRelative } from "./adminUi";

const RANGES = [
  { key: "7d",  label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "12m", label: "12 months" },
];

// Attention items that point at a group of users open the users tab filtered
const FILTER_FOR = { complimentary_ending: "complimentary", renewals: "ending", auto_trading_expired: "autotrading" };

const axis = {
  ticks: { color: COLORS.dim, font: { size: 10 } },
  grid: { color: COLORS.grid },
  border: { display: false },
};

const chartOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: { legend: { labels: { color: COLORS.muted, boxWidth: 10, font: { size: 11 } } } },
  scales: { x: { ...axis, stacked: true }, y: { ...axis, stacked: true, beginAtZero: true, ticks: { ...axis.ticks, precision: 0 } } },
  ...extra,
});

function SubscribersChart({ months }) {
  const labels = months.map((m) => new Date(`${m.month}-01T12:00:00Z`).toLocaleDateString("en-GB", { month: "short" }));
  return (
    <Bar
      data={{
        labels,
        datasets: [
          { type: "line", label: "Active subscribers", data: months.map((m) => m.activeSubscribers), borderColor: COLORS.text,
            backgroundColor: COLORS.text, pointRadius: 2, tension: 0.25, yAxisID: "y1", stack: "line" },
          { label: "New", data: months.map((m) => m.new), backgroundColor: COLORS.accent, stack: "moves" },
          { label: "Renewed", data: months.map((m) => m.renewed), backgroundColor: COLORS.blue, stack: "moves" },
          { label: "Churned", data: months.map((m) => -m.churned), backgroundColor: COLORS.danger, stack: "moves" },
        ],
      }}
      options={chartOptions({
        scales: {
          x: { ...axis, stacked: true },
          y: { ...axis, stacked: true, ticks: { ...axis.ticks, precision: 0 }, title: { display: true, text: "Movements", color: COLORS.dim, font: { size: 10 } } },
          y1: { ...axis, position: "right", beginAtZero: true, grid: { display: false }, ticks: { ...axis.ticks, precision: 0 } },
        },
      })}
    />
  );
}

function LoginsChart({ series, weekly }) {
  const labels = series.map((s) => new Date(`${s.date}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }));
  return (
    <Bar
      data={{
        labels,
        datasets: [
          { label: weekly ? "Logins per week" : "Logins", data: series.map((s) => s.logins), backgroundColor: COLORS.accent },
          { label: "Failed", data: series.map((s) => s.failedLogins), backgroundColor: COLORS.danger },
        ],
      }}
      options={chartOptions()}
    />
  );
}

function Funnel({ funnel }) {
  const steps = [
    ["Accounts", funnel.accounts],
    ["Ever subscribed", funnel.subscribed],
    ["Paying now", funnel.paying],
    ["Paying, automatic trading", funnel.autoTrading],
  ];
  const max = Math.max(1, funnel.accounts);
  return (
    <ol className="adm-funnel">
      {steps.map(([label, n], i) => (
        <li key={label}>
          <div className="adm-funnel__head">
            <span>{label}</span>
            <span className="adm-funnel__value">
              {n}
              {i > 0 && steps[i - 1][1] > 0 && <small> · {Math.round((n / steps[i - 1][1]) * 100)} %</small>}
            </span>
          </div>
          <span className="adm-funnel__bar"><span style={{ width: `${(n / max) * 100}%` }} /></span>
        </li>
      ))}
    </ol>
  );
}

const ACTIVITY = {
  signup:       { tone: "accent", title: (a) => `${a.name || a.email} signed up` },
  login:        { tone: "dim",    title: (a) => `${a.name || a.email} logged in` },
  login_failed: { tone: "danger", title: (a) => `Failed login for ${a.email || "unknown"}` },
  subscribed:   { tone: "accent", title: (a) => `${a.name || a.email} subscribed` },
  renewed:      { tone: "blue",   title: (a) => `${a.name || a.email} renewed` },
  cancelled:    { tone: "danger", title: (a) => `${a.name || a.email} cancelled` },
  run:          { tone: "dim",    title: () => "Nightly fund run" },
  run_failed:   { tone: "danger", title: () => "Nightly fund run failed" },
  email:        { tone: "blue",   title: () => "Emails sent" },
};

function Activity({ items, onOpenUser }) {
  if (!items.length) return <p className="adm-note">No activity yet.</p>;
  return (
    <ul className="adm-feed">
      {items.map((a, i) => {
        const kind = ACTIVITY[a.kind] || { tone: "dim", title: () => a.kind };
        return (
          <li key={`${a.at}-${i}`} className={`adm-feed__item adm-feed__item--${kind.tone}`}>
            <span className="adm-feed__dot" aria-hidden="true" />
            <div className="adm-feed__body">
              {a.userId ? (
                <button type="button" className="adm-link" onClick={() => onOpenUser(a.userId)}>{kind.title(a)}</button>
              ) : (
                <span>{kind.title(a)}</span>
              )}
              {a.count > 1 && <span className="adm-count">×{a.count}</span>}
              {a.detail && <span className="adm-feed__detail">{a.detail}</span>}
            </div>
            <time dateTime={a.at} title={formatDateTime(a.at)} className="adm-feed__time">{formatRelative(a.at)}</time>
          </li>
        );
      })}
    </ul>
  );
}

function Attention({ items, system, onOpenUser, onFilterUsers }) {
  return (
    <>
      <ul className="adm-attention">
        {items.map((item, i) => {
          const target = item.userIds?.length === 1 ? () => onOpenUser(item.userIds[0])
            : FILTER_FOR[item.kind] ? () => onFilterUsers(FILTER_FOR[item.kind]) : null;
          return (
            <li key={i} className={`adm-attention__item adm-attention__item--${item.severity}`}>
              <span className="adm-attention__icon" aria-hidden="true">{item.severity === "ok" ? "✓" : item.severity === "info" ? "i" : "!"}</span>
              {target ? <button type="button" className="adm-link" onClick={target}>{item.title}</button> : <span>{item.title}</span>}
            </li>
          );
        })}
      </ul>
      {system?.lastRun && (
        <p className="adm-note adm-system">
          Last run {formatRelative(system.lastRun.finishedAt)}
          {system.lastRun.durationS != null && ` · ${system.lastRun.durationS} s`}
          {/* the model fund is in USD; billing (MRR, payments) stays in CHF */}
          {system.lastRun.finalAum != null && ` · AUM ${formatMoney(system.lastRun.finalAum, system.lastRun.currency || "USD")}`}
          {system.lastEmails && ` · ${system.lastEmails.count} email${system.lastEmails.count === 1 ? "" : "s"} sent`}
        </p>
      )}
    </>
  );
}

function OverviewSkeleton() {
  return (
    <div aria-busy="true">
      <div className="adm-kpis">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="adm-kpi"><Skeleton className="adm-skeleton--label" /><Skeleton className="adm-skeleton--value" /></div>)}</div>
      <div className="adm-grid adm-grid--2">{[0, 1].map((i) => <div key={i} className="adm-panel"><Skeleton className="adm-skeleton--chart" /></div>)}</div>
    </div>
  );
}

const AdminOverview = ({ auth, onOpenUser, onFilterUsers }) => {
  const [range, setRange]         = useState("30d");
  const [metrics, setMetrics]     = useState(null);
  const [error, setError]         = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setError(null);
      try {
        const res = await fetch(`/api/admin/metrics?range=${range}`, {
          headers: { Authorization: "Bearer " + auth.token },
          signal: controller.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        setMetrics(data);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      }
    };
    load();
    return () => controller.abort();
  }, [auth.token, range, reloadKey]);

  const rangeLabel = RANGES.find((r) => r.key === range).label;
  const k = metrics?.kpis;
  const spark = (field) => metrics?.series.map((s) => s[field]);

  return (
    <section aria-label="Overview">
      <div className="adm-toolbar">
        <div className="adm-segmented" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button key={r.key} type="button" aria-pressed={range === r.key}
                    className={range === r.key ? "is-active" : ""} onClick={() => setRange(r.key)}>
              {r.label}
            </button>
          ))}
        </div>
        <span className="adm-note">Changes compared with the previous {rangeLabel}</span>
      </div>

      {error && (
        <div className="adm-panel adm-message" role="alert">
          <p>Couldn't load the dashboard: {error}</p>
          <button type="button" className="adm-btn adm-btn--outline" onClick={() => setReloadKey((n) => n + 1)}>Try again</button>
        </div>
      )}
      {!error && !metrics && <OverviewSkeleton />}
      {!error && metrics && (
        <>
          <div className="adm-kpis">
            <KpiCard label="MRR" value={formatChf(k.mrr.value)} spark={spark("mrr")}
                     delta={<Delta value={k.mrr.value} previous={k.mrr.previous} format={(v) => formatChf(v)} />}
                     sub="accounts with a Stripe subscription" />
            <KpiCard label="Active subscribers" value={k.activeSubscribers.value} spark={spark("activeSubscribers")}
                     delta={<Delta value={k.activeSubscribers.value} previous={k.activeSubscribers.previous} />}
                     sub={`${k.activeSubscribers.paying} paying · ${k.activeSubscribers.complimentary} complimentary`} />
            <KpiCard label="New signups" value={k.signups.value} spark={spark("signups")} sparkColor={COLORS.blue}
                     delta={<Delta value={k.signups.value} previous={k.signups.previous} />} />
            <KpiCard label="Conversion" value={`${k.conversion.converted} of ${k.conversion.of}`}
                     sub="of this period's signups subscribed" />
            <KpiCard label="Churned" value={k.churned.value}
                     delta={<Delta value={k.churned.value} previous={k.churned.previous} goodWhen="down" />}
                     sub="access ended without renewal" />
            <KpiCard label="Logins" value={k.logins.value} spark={spark("logins")}
                     delta={<Delta value={k.logins.value} previous={k.logins.previous} />}
                     sub={<><span className={k.logins.failed ? "adm-text-danger" : ""}>{k.logins.failed} failed</span> · {k.logins.activeUsers} active user{k.logins.activeUsers === 1 ? "" : "s"}</>} />
          </div>

          <div className="adm-grid adm-grid--2">
            <div className="adm-panel">
              <h2 className="adm-panel__title">Subscribers &amp; movements <small>last 12 months</small></h2>
              <div className="adm-chart"><SubscribersChart months={metrics.months} /></div>
            </div>
            <div className="adm-panel">
              <h2 className="adm-panel__title">Login activity <small>{rangeLabel}</small></h2>
              <div className="adm-chart"><LoginsChart series={metrics.series} weekly={range === "12m"} /></div>
            </div>
          </div>

          <div className="adm-grid adm-grid--3">
            <div className="adm-panel">
              <h2 className="adm-panel__title">Funnel</h2>
              <Funnel funnel={metrics.funnel} />
            </div>
            <div className="adm-panel">
              <h2 className="adm-panel__title">Needs attention</h2>
              <Attention items={metrics.attention} system={metrics.system} onOpenUser={onOpenUser} onFilterUsers={onFilterUsers} />
            </div>
            <div className="adm-panel">
              <h2 className="adm-panel__title">Recent activity</h2>
              <Activity items={metrics.activity} onOpenUser={onOpenUser} />
            </div>
          </div>
          <p className="adm-note adm-footnote">Updated {formatRelative(metrics.generatedAt)} · figures refresh at most once a minute</p>
        </>
      )}

      {/* The same information as lists, with revenue from Stripe */}
      <AdminDetails auth={auth} onOpenUser={onOpenUser} />
    </section>
  );
};

export default AdminOverview;
